import { html, LitElement } from "lit"
import { customElement, property } from "lit/decorators.js"
import mapboxgl from "mapbox-gl"
import { MapControlHost } from "./map-control-host.ts"
import {
  applyDepth,
  applyLightPalette,
  BASEMAP_IMPORT_ID,
  CONFIG_LIGHT_PRESET,
  CONFIG_SHOW_POI_LABELS,
  type MapStyle,
  STANDARD_OVERLAY_SLOT,
  STYLE_STORAGE_KEY,
  STYLE_URLS,
} from "./map-styles.ts"
import { type LightPreset, poiCamera, poiLight, tripLight } from "../map/camera.ts"
import { ensureMapStylesheet } from "../map/bridge.ts"
import {
  MAP_EVENT,
  mapEvent,
  type MapGesture,
  type MapMultiLineString,
  type MapPoi,
  parseMapPois,
  parseMapRoute,
  poiLngLat,
  type SerializedJson,
} from "../map/types.ts"
import type { PoiType } from "../pois/db.ts"
import {
  GEOGRAPHIC_TYPES,
  type GeographicType,
  POI_TYPE_ICONS as POI_ICONS,
} from "../pois/labels.ts"

const ROUTE_SOURCE_ID = "trip-route"
const ROUTE_LAYER_ID = "trip-route-line"

/** How visible a marker the terrain is hiding stays — see `_renderMarkers`. */
const OCCLUDED_OPACITY = 0.15

/**
 * How far the camera swings around a POI while the player dwells on it.
 *
 * The duration comes from the caller, which passes the dwell it is gating, so
 * the drift always takes exactly as long as the viewer is asked to look.
 *
 * A still frame held for two to eight seconds reads as a stall, and parallax
 * over terrain is most of what makes a pitched view feel like a place rather
 * than a picture of one. Small enough that it reads as a drift rather than a
 * move: the viewer is looking at the POI, not being taken somewhere.
 */
const ORBIT_DEGREES = 14

/**
 * How close a plain, non-cinematic flight gets when the camera is further out.
 *
 * A marker click from the trip overview has to arrive somewhere, so a flight
 * with no opinion about zoom still needs a floor. It is a floor rather than a
 * setting because the viewer's own zoom outranks it: someone already closer
 * than this asked to be there, and yanking them back out to a fixed level is
 * the thing that made a pin click feel like it undid their last gesture.
 */
const BROWSE_ZOOM = 14

/**
 * How far a viewer's own zooming is carried from one POI to the next.
 *
 * During a run the camera's zoom is policy — `camera.ts` sets it from the lens
 * the photo was taken with — but a viewer who pulls back to see where they are
 * has said something the next POI should respect. The bias is the gap between
 * what the last arrival asked for and where the viewer left the camera, and it
 * rides along on every arrival after it.
 *
 * Clamped because it is applied blind: a pinch that bottoms out at the world
 * view must not turn the rest of the trip into a globe.
 */
const ZOOM_BIAS_LIMIT = 3

const POI_COLORS: Record<string, string> = {
  photo: "#3b82f6",
  video: "#8b5cf6",
  water_source: "#06b6d4",
  food_cache: "#f59e0b",
  trail_junction: "#10b981",
  camp_site: "#ef4444",
}

@customElement("trip-map")
export class TripMap extends MapControlHost(LitElement) {
  // Disable shadow DOM so Mapbox GL CSS and external styles apply directly
  override createRenderRoot() {
    return this
  }

  @property({ attribute: "token" })
  accessor token: string = ""

  @property({ attribute: "route" })
  accessor route: string = ""

  @property({ attribute: "pois" })
  accessor poisJson: string = "[]"

  @property({ attribute: "height" })
  accessor height: string = "400px"

  @property({ attribute: "style-name" })
  accessor styleName: string = "terrain"

  @property({ attribute: "editable-poi-id" })
  accessor editablePoiId: string = ""

  @property({ attribute: "bounds-pois" })
  accessor boundsPoisJson: string = ""

  private _markers: mapboxgl.Marker[] = []
  /**
   * The same markers by POI id, so `flyToPoi` can mark the one it flew to
   * without re-deriving it. The halo is a side effect *inside* the element
   * precisely because the id→marker resolution already lives here.
   */
  private _markersById = new Map<string, mapboxgl.Marker>()
  /** Set by `setPois` so the next marker re-render leaves the viewport alone. */
  private _skipFitForPois = false
  /** The POI the camera was last flown to — what the halo, or its faded form, marks. */
  private _cameraPoiId: string | null = null
  /** Whether the camera's POI is currently haloed rather than merely faded. */
  private _haloed = false
  /**
   * The zoom `camera.ts` asked for at the last cinematic arrival, before any
   * bias — the reference the viewer's own zooming is measured against.
   */
  private _policyZoom: number | null = null
  /** How far the viewer has zoomed away from `_policyZoom`; see `ZOOM_BIAS_LIMIT`. */
  private _zoomBias = 0
  /** Whether every marker but the camera's is hidden — see `setSoloMarker`. */
  private _soloMarker = false
  /** Standard style config the app wants, re-asserted whenever the style changes. */
  private _desiredConfig = new Map<string, unknown>()
  private _routeLoaded = false
  private _routeRenderPending = false
  private _placeClickHandler: ((e: mapboxgl.MapMouseEvent) => void) | null = null

  // Long-press detection
  private _lpTimer: ReturnType<typeof setTimeout> | null = null
  private _lpStartPoint: { x: number; y: number } | null = null
  private _lpStartLngLat: mapboxgl.LngLat | null = null
  private static readonly LP_DURATION_MS = 600
  private static readonly LP_MOVE_TOLERANCE_PX = 10

  private _getInitialStyle(): MapStyle {
    try {
      const stored = localStorage.getItem(STYLE_STORAGE_KEY) as MapStyle | null
      if (stored && stored in STYLE_URLS) return stored
    } catch {
      // localStorage unavailable
    }
    const attr = this.styleName as MapStyle
    return attr in STYLE_URLS ? attr : "terrain"
  }

  override connectedCallback() {
    super.connectedCallback()
    void ensureMapStylesheet()
  }

  override disconnectedCallback() {
    super.disconnectedCallback()
    this._map?.remove()
    this._map = null
  }

  override firstUpdated() {
    if (!this.token) return

    const container = this.querySelector<HTMLDivElement>(".trip-map-container")
    if (!container) return

    const initialStyle = this._getInitialStyle()
    const initialBounds = this._computeInitialBounds()

    this._map = new mapboxgl.Map({
      container,
      accessToken: this.token,
      style: STYLE_URLS[initialStyle],
      ...(initialBounds
        ? {
          bounds: initialBounds,
          fitBoundsOptions: { padding: 60, maxZoom: 14, animate: false },
        }
        : { zoom: 10, center: [0, 0] as [number, number] }),
      performanceMetricsCollection: false,
    })

    this._map.addControl(new mapboxgl.NavigationControl(), "top-right")

    // Prefetch the trip's light, before the map is interactive.
    //
    // Flipping `lightPreset` costs a 190-550ms frozen frame and does not
    // amortise (measured — see `wiki/findings/mapbox-config-repaint-loop.md`).
    // That is survivable once and intolerable under a viewer's thumb, so the
    // one flip a trip needs is spent here, during style load, where nothing is
    // animating and the map is not yet accepting gestures. By the time a POI
    // is focused the preset it wants is usually already standing, and
    // `_applyConfig`'s guard makes re-asserting it free.
    //
    // Seeded *before* `load` rather than inside it, and paired with the
    // `styledata` re-assert below, because `setConfigProperty` throws while the
    // style is mid-load: this records desired state and lets the first
    // `styledata` that can accept it land the value.
    this._desiredConfig.set(CONFIG_LIGHT_PRESET, tripLight(this._parsePois()))
    this._map.on("styledata", () => this._applyConfig())

    this._map.on("load", () => {
      if (!this._map) return

      // DEM source, terrain mesh, hillshade and fog, in one call — see
      // `applyDepth`. `readdDepth` runs the same thing after a style switch.
      applyDepth(this._map, initialStyle, this._light())

      this._renderRoute()
      this._renderMarkers()
      this._fitViewport()

      this._installLongPress()
      this._installCameraEvents()

      this._flushAndReady()
    })
  }

  override updated(changed: Map<string, unknown>) {
    if (!this._map) return
    if (changed.has("route")) {
      this._renderRoute()
      if (!changed.has("poisJson")) this._fitViewport()
    }
    if (changed.has("poisJson")) {
      this._renderMarkers()
      // A live edit moves one marker; it does not re-frame the trip. Only a
      // page-level POI payload (a fresh render, a route change) refits.
      if (this._skipFitForPois) this._skipFitForPois = false
      else this._fitViewport()
    }
  }

  // ---------------------------------------------------------------------------
  // Route rendering
  // ---------------------------------------------------------------------------

  private _renderRoute() {
    if (!this._map) return

    // `updated()` can fire (e.g. a client-side re-render when the POI edit
    // drawer opens) while the Mapbox style is still loading. `addSource` /
    // `addLayer` throw "Style is not done loading" in that window, so defer the
    // render until the style is ready. A single pending flag avoids stacking
    // listeners across repeated calls.
    if (!this._map.isStyleLoaded()) {
      if (this._routeRenderPending) return
      this._routeRenderPending = true
      this._map.once("styledata", () => {
        this._routeRenderPending = false
        this._renderRoute()
      })
      return
    }

    const geojson = this._parseRoute()

    if (this._map.getSource(ROUTE_SOURCE_ID)) {
      ;(this._map.getSource(ROUTE_SOURCE_ID) as mapboxgl.GeoJSONSource).setData(
        geojson ?? { type: "FeatureCollection", features: [] },
      )
      return
    }

    if (!geojson) return

    this._map.addSource(ROUTE_SOURCE_ID, {
      type: "geojson",
      data: geojson,
    })

    this._map.addLayer({
      id: ROUTE_LAYER_ID,
      type: "line",
      source: ROUTE_SOURCE_ID,
      slot: STANDARD_OVERLAY_SLOT,
      layout: { "line-join": "round", "line-cap": "round" },
      paint: { "line-color": "#3b82f6", "line-width": 3, "line-opacity": 0.8 },
    })

    this._routeLoaded = true
  }

  private _parseRoute(): MapMultiLineString | null {
    return parseMapRoute(this.route)
  }

  /** Re-adds route source + layer after a style change (setStyle clears them). */
  readdRoute() {
    const geojson = this._parseRoute()
    if (!this._map || !geojson) return

    if (!this._map.getSource(ROUTE_SOURCE_ID)) {
      this._map.addSource(ROUTE_SOURCE_ID, { type: "geojson", data: geojson })
    }
    if (!this._map.getLayer(ROUTE_LAYER_ID)) {
      this._map.addLayer({
        id: ROUTE_LAYER_ID,
        type: "line",
        source: ROUTE_SOURCE_ID,
        slot: STANDARD_OVERLAY_SLOT,
        layout: { "line-join": "round", "line-cap": "round" },
        paint: {
          "line-color": "#3b82f6",
          "line-width": 3,
          "line-opacity": 0.8,
        },
      })
    }
  }

  // ---------------------------------------------------------------------------
  // Marker rendering
  // ---------------------------------------------------------------------------

  private _renderMarkers() {
    if (!this._map) return

    // Remove existing markers
    for (const m of this._markers) m.remove()
    this._markers = []
    this._markersById.clear()

    const pois = this._parsePois()
    let editableHasCoords = false

    for (const poi of pois) {
      const at = poiLngLat(poi)
      if (!at) continue
      const { lat, lng } = at

      const isEditable = this.editablePoiId !== "" && poi.id === this.editablePoiId
      if (isEditable) editableHasCoords = true

      this._addPoiMarker(poi, lng, lat, isEditable)
    }

    // No marker for the editable POI yet — wait for the user's first click.
    if (this.editablePoiId && !editableHasCoords) {
      this._enablePlaceOnClick()
    } else {
      this._disablePlaceOnClick()
    }
  }

  private _addPoiMarker(
    poi: MapPoi,
    lng: number,
    lat: number,
    isEditable: boolean,
  ) {
    if (!this._map) return

    const el = document.createElement("div")
    // The halo and its faded form are styled from `assets/styles.css` against
    // this class — the element renders into the light DOM, so page CSS reaches
    // it. Everything the marker *is* stays inline below, untouched by both.
    el.className = "poi-marker"
    el.style.cssText = `
      width: 32px; height: 32px; border-radius: 50%;
      background: ${POI_COLORS[poi.type] ?? "#6b7280"};
      display: flex; align-items: center; justify-content: center;
      font-size: 14px; border: 2px solid white;
      box-shadow: 0 2px 4px rgba(0,0,0,0.3);
      cursor: ${isEditable ? "grab" : "pointer"};
    `
    el.textContent = POI_ICONS[poi.type as PoiType] ?? "📍"

    // `occludedOpacity` fades a marker the terrain is standing in front of.
    // It costs nothing while the camera is overhead and becomes load-bearing
    // the moment the Trip Player pitches: a DOM marker does not respect depth
    // on its own, so a POI behind a ridge would otherwise float on top of the
    // mountain and read as broken. Requires active terrain, which is set on
    // load.
    const marker = new mapboxgl.Marker({
      element: el,
      draggable: isEditable,
      occludedOpacity: OCCLUDED_OPACITY,
    })
      .setLngLat([lng, lat])
      .addTo(this._map)

    if (isEditable) {
      marker.on("dragend", () => {
        const { lng: newLng, lat: newLat } = marker.getLngLat()
        this.dispatchEvent(
          mapEvent(MAP_EVENT.poiDragged, {
            id: poi.id,
            lat: newLat,
            lng: newLng,
          }),
        )
      })
    } else {
      const popup = new mapboxgl.Popup({
        offset: 20,
        closeButton: false,
      }).setHTML(this._buildPopupHtml(poi))
      marker.setPopup(popup)

      el.addEventListener("mouseenter", () => popup.addTo(this._map!))
      el.addEventListener("mouseleave", () => {
        setTimeout(() => {
          if (!popup.getElement()?.matches(":hover")) popup.remove()
        }, 150)
      })

      el.addEventListener("click", () => {
        this.dispatchEvent(mapEvent(MAP_EVENT.poiFocused, { id: poi.id, lat, lng }))
      })
    }

    this._markers.push(marker)
    this._markersById.set(poi.id, marker)
    this._applyMarkerTreatment()
  }

  private _enablePlaceOnClick() {
    if (!this._map || this._placeClickHandler) return
    this._map.getCanvas().style.cursor = "crosshair"

    this._placeClickHandler = (e: mapboxgl.MapMouseEvent) => {
      const { lng, lat } = e.lngLat
      this._disablePlaceOnClick()

      // Render a draggable marker immediately so the user can refine.
      const pois = this._parsePois()
      const editable = pois.find((p) => p.id === this.editablePoiId)
      if (editable) this._addPoiMarker(editable, lng, lat, true)

      this.dispatchEvent(
        mapEvent(MAP_EVENT.poiPlaced, { id: this.editablePoiId, lat, lng }),
      )
    }
    this._map.on("click", this._placeClickHandler)
  }

  private _installLongPress() {
    if (!this._map) return
    const map = this._map

    const cancel = () => {
      if (this._lpTimer !== null) {
        clearTimeout(this._lpTimer)
        this._lpTimer = null
      }
      this._lpStartPoint = null
      this._lpStartLngLat = null
    }

    const start = (e: mapboxgl.MapMouseEvent | mapboxgl.MapTouchEvent) => {
      // Don't compete with the edit-drawer's click-to-place behavior.
      if (this.editablePoiId) return
      cancel()
      this._lpStartPoint = { x: e.point.x, y: e.point.y }
      this._lpStartLngLat = e.lngLat
      this._lpTimer = setTimeout(() => {
        this._lpTimer = null
        const lngLat = this._lpStartLngLat
        this._lpStartLngLat = null
        this._lpStartPoint = null
        if (!lngLat) return
        try {
          ;(navigator as { vibrate?: (n: number) => void }).vibrate?.(20)
        } catch {
          // ignore — vibration may be blocked
        }
        this.dispatchEvent(
          mapEvent(MAP_EVENT.gpsPicked, { lat: lngLat.lat, lng: lngLat.lng }),
        )
      }, TripMap.LP_DURATION_MS)
    }

    const move = (e: mapboxgl.MapMouseEvent | mapboxgl.MapTouchEvent) => {
      if (!this._lpStartPoint) return
      const dx = e.point.x - this._lpStartPoint.x
      const dy = e.point.y - this._lpStartPoint.y
      if (
        dx * dx + dy * dy >
          TripMap.LP_MOVE_TOLERANCE_PX * TripMap.LP_MOVE_TOLERANCE_PX
      ) {
        cancel()
      }
    }

    map.on("mousedown", start)
    map.on("touchstart", start)
    map.on("mousemove", move)
    map.on("touchmove", move)
    map.on("mouseup", cancel)
    map.on("touchend", cancel)
    map.on("touchcancel", cancel)
    map.on("dragstart", cancel)
    map.on("movestart", cancel)
  }

  /**
   * Mirror Mapbox's camera events onto the element.
   *
   * `moveend` is passed through faithfully — programmatic flights and thumbs
   * alike — because the Trip Player gates its dwell on it and a synthesized
   * settle would advance the playhead while the camera flew somewhere else.
   * The user-move signal is the discriminator: Mapbox marks a gesture-driven
   * move with `originalEvent`, and nothing else carries one.
   *
   * The four per-gesture starts are listened to rather than `movestart`,
   * because the player wants to know *which* gesture it was and `movestart`
   * cannot say — Mapbox fires it once, before the specific starts, for
   * whichever combination of pan, zoom, rotate and pitch a gesture produced.
   * Nothing is lost by dropping it: every gesture yields at least one delta,
   * so it always lands on one of these four.
   */
  private _installCameraEvents() {
    if (!this._map) return
    const map = this._map

    map.on("moveend", () => {
      this.dispatchEvent(mapEvent(MAP_EVENT.moveEnd, {}))
    })

    // Mapbox's own typings omit `originalEvent` on these even though it is
    // there at runtime, so the discriminator is read structurally.
    const gesture = (kind: MapGesture) => (e: unknown) => {
      if (!(e as { originalEvent?: unknown } | null)?.originalEvent) return
      this.dispatchEvent(mapEvent(MAP_EVENT.userMove, { kind }))
    }
    map.on("dragstart", gesture("pan"))
    map.on("zoomstart", gesture("zoom"))
    map.on("rotatestart", gesture("rotate"))
    map.on("pitchstart", gesture("pitch"))

    // A viewer's zoom is remembered, not just allowed: see `_zoomBias`. Read
    // at `zoomend` rather than per-frame so a pinch is one adjustment rather
    // than a hundred, and only against a cinematic arrival — with no run in
    // progress there is no policy zoom to be biased away from.
    map.on("zoomend", (e: unknown) => {
      if (!(e as { originalEvent?: unknown } | null)?.originalEvent) return
      if (this._policyZoom === null) return
      const drift = map.getZoom() - this._policyZoom
      this._zoomBias = Math.max(-ZOOM_BIAS_LIMIT, Math.min(ZOOM_BIAS_LIMIT, drift))
    })
  }

  private _disablePlaceOnClick() {
    if (!this._map || !this._placeClickHandler) return
    this._map.off("click", this._placeClickHandler)
    this._map.getCanvas().style.cursor = ""
    this._placeClickHandler = null
  }

  private _parsePois(): MapPoi[] {
    return parseMapPois(this.poisJson)
  }

  private _buildPopupHtml(poi: MapPoi): string {
    const thumb = poi.signed_thumbnail ??
      poi.signed_optimized ??
      poi.signed_original ??
      poi.signed_video_thumbnail
    const isGeo = GEOGRAPHIC_TYPES.has(poi.type as GeographicType)
    const stars = poi.rating ? "★".repeat(poi.rating) + "☆".repeat(5 - poi.rating) : ""

    let out = `<div style="font-family:sans-serif;min-width:160px;max-width:220px">`
    if (thumb) {
      out += `<img src="${
        esc(
          thumb,
        )
      }" width="400" height="400" loading="lazy" decoding="async" style="width:100%;height:120px;object-fit:cover;border-radius:4px;margin-bottom:8px">`
    }
    if (poi.name) {
      out += `<div style="font-weight:600;font-size:13px;margin-bottom:4px">${
        esc(
          poi.name,
        )
      }</div>`
    }
    if (poi.notes && isGeo) {
      const snippet = poi.notes.length > 80 ? poi.notes.slice(0, 80) + "…" : poi.notes
      out += `<div style="font-size:12px;color:#4b5563;margin-bottom:4px">${
        esc(
          snippet,
        )
      }</div>`
    }
    if (stars) {
      out += `<div style="font-size:12px;color:#f59e0b">${esc(stars)}</div>`
    }
    out += `</div>`
    return out
  }

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  get isMoving(): boolean {
    return this._map?.isMoving() ?? false
  }

  /**
   * Fly the camera, and say whether it went.
   *
   * The boolean is what lets a caller waiting on `move-end` tell a real flight
   * from a dropped one: a step that silently returned here would otherwise
   * wait forever for a settle that is never coming. Both silent returns are
   * older than the return type — the map may not be built yet, and Mapbox
   * drops a `flyTo` issued while another is in the air.
   */
  flyTo(
    lat: number,
    lng: number,
    options?: {
      zoom?: number
      speed?: number
      offset?: [number, number]
      pitch?: number
      bearing?: number
    },
  ): boolean {
    if (!this._map || this._map.isMoving()) return false
    this._map.flyTo({
      center: [lng, lat],
      speed: options?.speed ?? 1.2,
      // Every optional camera field is omitted rather than passed as
      // `undefined`. That is a correctness requirement, not a style: Mapbox
      // reads `options.offset` through `Point.convert` without a guard, so an
      // explicit `undefined` throws where an absent key takes the default.
      // It is also the right default for pitch and bearing — a caller with no
      // opinion about where to look should leave the camera where the viewer
      // put it, since turning to due north and flattening to overhead are both
      // moves, and a move reads as meaning something.
      //
      // Zoom is omitted on the same terms, and it used to be the exception: a
      // hard-coded 14 meant every flight with no opinion re-framed the map,
      // so a viewer who had zoomed in to read the terrain lost it to the next
      // pin click or panel scroll. Callers that do need a floor ask for one —
      // see `BROWSE_ZOOM`.
      ...(options?.zoom !== undefined ? { zoom: options.zoom } : {}),
      ...(options?.offset !== undefined ? { offset: options.offset } : {}),
      ...(options?.pitch !== undefined ? { pitch: options.pitch } : {}),
      ...(options?.bearing !== undefined ? { bearing: options.bearing } : {}),
    })
    return true
  }

  /**
   * Drift the camera around the subject it is already looking at.
   *
   * Used for the Trip Player's dwell, and deliberately *not* a flight: it does
   * not re-centre, and the caller must not treat its `moveend` as an arrival.
   * `TripMapCoordinator` is what keeps those apart — this element reports
   * Mapbox's camera events faithfully and does not editorialise about which of
   * its own motions they came from.
   */
  orbit(ms: number): boolean {
    if (!this._map || ms <= 0) return false
    // A viewer who asked for less motion is not asking for a camera that
    // wanders while they read. Checked here rather than at the call site so
    // every caller inherits it — and reported back, because a caller waiting
    // to discount this orbit's `move-end` must know one is coming. Mapbox
    // would otherwise collapse the ease to a zero-duration jump, which is a
    // pointless 14-degree snap.
    if (globalThis.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      return false
    }
    this._map.easeTo({
      bearing: this._map.getBearing() + ORBIT_DEGREES,
      duration: ms,
      easing: (t) => t,
    })
    return true
  }

  /**
   * Stop whatever the camera is doing, now.
   *
   * The player calls this before every step. `flyTo` drops a flight issued
   * while another motion is in the air, so without it the dwell's orbit would
   * silently swallow the next POI's flight — the exact failure the settle gate
   * exists to make impossible.
   */
  stopMotion(): void {
    this._map?.stop()
  }

  /**
   * Light the basemap for a moment in the trip, or restore the default.
   *
   * Both styles are Mapbox Standard variants, so this is valid whichever is
   * showing. A photo taken at sunset arriving with the map lit at dusk costs
   * one call and does more for the sense of being there than any amount of
   * camera work.
   */
  setLight(preset: LightPreset | null): void {
    const light = preset ?? this._tripLight()
    this._configure(CONFIG_LIGHT_PRESET, light)
    // Standard's own fog moves with `lightPreset`; ours does not, because
    // `setFog` replaced it. Repaint the atmosphere and the relief tints to
    // match, or a night step keeps a noon horizon.
    if (this._map) applyLightPalette(this._map, light)
  }

  /**
   * The light this trip is shown in when no single POI is the subject.
   *
   * `null` reaches `setLight` from two places — the Lead-in, and a POI whose
   * time cannot be known — and both mean "no particular moment in the trip",
   * which is a different claim from "noon".
   */
  private _tripLight(): LightPreset {
    return tripLight(this._parsePois())
  }

  /** The `lightPreset` currently asked for — what the depth palette keys off. */
  private _light(): LightPreset {
    return (this._desiredConfig.get(CONFIG_LIGHT_PRESET) as LightPreset) ?? "day"
  }

  /**
   * Re-add the depth stack after a style change (`setStyle` clears all of it).
   *
   * The sibling of `readdRoute`, and called from the same place —
   * `map-style-switcher`, which owns the `setStyle` but not the current light.
   */
  readdDepth(style: MapStyle): void {
    if (this._map) applyDepth(this._map, style, this._light())
  }

  /**
   * Hide or restore the basemap's own place-of-interest labels.
   *
   * Mapbox's POI labels and the trip's markers claim the same role, and during
   * playback only one of them is the subject.
   */
  setBasemapPoiLabels(visible: boolean): void {
    this._configure(CONFIG_SHOW_POI_LABELS, visible)
  }

  /**
   * Record a Standard style config value and assert it on the map.
   *
   * Desired state, not a one-shot command, and re-asserted on every
   * `styledata`. Two things make that necessary rather than fussy.
   *
   * `setConfigProperty` throws while the style is mid-load, and these settings
   * are driven by state that mostly settles *before* that — on an autoplayed
   * page the transport goes `Idle` then `Playing` within the first moments,
   * both too early. Deferring to `style.load` did not save it either:
   * `isStyleLoaded()` reads false transiently while the map is busy, so the
   * wait was registered for an event that had already fired and would not fire
   * again, and the basemap's own labels stayed up for the whole run.
   *
   * The second is `map-style-switcher`, which calls `setStyle` — that resets
   * every config to the new style's defaults. Re-asserting means a switch from
   * terrain to satellite mid-run keeps the run's lighting and its hidden
   * labels, instead of silently reverting them.
   */
  private _configure(key: string, value: unknown): void {
    this._desiredConfig.set(key, value)
    this._applyConfig()
  }

  /**
   * Push every desired config value at the style, ignoring one not ready yet.
   *
   * Each write is guarded by a read, and that guard is what keeps this method
   * from pegging a core. `setConfigProperty` does no equality check of its own:
   * it sets the option and calls `Map._update(true)` unconditionally, which
   * marks the style dirty and schedules a repaint, and that repaint fires
   * `styledata` — the event this runs on. Re-asserting a value the style
   * already holds therefore closes a loop that re-renders the whole GL scene at
   * the display's refresh rate, forever, on a map nobody is touching. Measured
   * at ~120 styledata/second on an idle map; zero with the guard.
   *
   * Comparing against the map rather than against a cache of what was last
   * written is what keeps the re-assert honest. `setStyle` resets every config
   * to the new style's defaults without telling anyone, so a write-cache would
   * believe the value still stood and skip restoring it; reading the live value
   * means a style switch re-asserts exactly once and then goes quiet again.
   *
   * There is no regression test for this, on purpose — the bug needs a real GL
   * context and is a rate rather than a return value, so every seam this repo
   * has would lie about it. See `wiki/findings/mapbox-config-repaint-loop.md`
   * for the measurements, the general rule, and how to rebuild the repro.
   */
  private _applyConfig(): void {
    const map = this._map
    if (!map) return
    for (const [key, value] of this._desiredConfig) {
      try {
        if (map.getConfigProperty(BASEMAP_IMPORT_ID, key) === value) continue
        map.setConfigProperty(BASEMAP_IMPORT_ID, key, value)
      } catch {
        // Style mid-load, or one without this config. Both settings are
        // flourishes — never let one take a flight down with it, and the next
        // `styledata` will try again.
      }
    }
  }

  /**
   * Fly to a POI by id, and halo its marker as the camera's subject.
   *
   * `null` is the two cases a bare id cannot express — a step onto a POI with
   * no coordinates, and the Lead-in, where there is no current POI at all.
   * Both clear the halo rather than leaving the last one lit over a camera
   * that has moved on.
   *
   * The camera itself — where to look, how far to tip, how tight to frame, and
   * what light to do it in — is `lib/map/camera.ts`'s decision, taken from the
   * POI and its place in the trip's sequence. An explicit `zoom` in
   * `options` still wins, which is what keeps the marker-click path free to
   * frame differently from a player step.
   */
  flyToPoi(
    id: string | null,
    options?: {
      zoom?: number
      speed?: number
      offset?: [number, number]
      /** Skip the pitch, turn and lighting — a plain overhead flight. */
      flat?: boolean
    },
  ): boolean {
    if (id === null) {
      this._applyHalo(null)
      return false
    }
    const pois = this._parsePois()
    const index = pois.findIndex((p) => p.id === id)
    const poi = index === -1 ? undefined : pois[index]
    const at = poi ? poiLngLat(poi) : null
    if (!poi || !at) {
      this._applyHalo(null)
      return false
    }
    this._applyHalo(id)

    if (options?.flat) {
      // `flat` withholds the pitch and the turn, not the light. Tipping the
      // camera 60 degrees because someone clicked a pin is a bigger change
      // than they asked for and leaves them no obvious way back to overhead —
      // that is what the flag is for. Lighting the basemap for the hour the
      // photo was taken is not that: it does not move the camera, it cannot
      // strand the viewer, and it is the same answer whether a click or a
      // player step asked the question.
      this.setLight(poiLight(poi))
      // Browsing keeps the viewer's zoom, only closing in when they are
      // further out than a pin is worth looking at — see `BROWSE_ZOOM`.
      const current = this._map?.getZoom() ?? BROWSE_ZOOM
      return this.flyTo(at.lat, at.lng, {
        ...options,
        zoom: options?.zoom ?? Math.max(current, BROWSE_ZOOM),
      })
    }

    const camera = poiCamera(pois, index, this._parseRoute())
    this.setLight(camera.light)
    const flown = this.flyTo(at.lat, at.lng, {
      ...options,
      // Policy, plus however far the viewer has zoomed away from it. Clamped
      // again here rather than trusting the stored bias, because the policy
      // zoom moves with the lens and the sum is what has to stay sane.
      zoom: options?.zoom ?? Math.max(1, Math.min(22, camera.zoom + this._zoomBias)),
      pitch: camera.pitch,
      ...(camera.bearing !== null ? { bearing: camera.bearing } : {}),
    })
    // Only a flight that went sets the reference the viewer's zoom is
    // measured against; a dropped one leaves the camera where it was, and so
    // leaves the last arrival's policy standing.
    if (flown) this._policyZoom = camera.zoom
    return flown
  }

  /**
   * Refit the viewport to the whole trip — the framing the map first loads with.
   *
   * Clears the marker treatment outright rather than fading it: the camera is
   * framing the whole trip now, so there is no subject left to mark, dimly or
   * otherwise.
   */
  resetViewport(): void {
    this._cameraPoiId = null
    this._applyHalo(null)
    // Reset is the page back to scratch, so the viewer's zoom adjustment goes
    // with it — a run restarted from the Lead-in frames its first POI the way
    // `camera.ts` asks, not the way the last run was left.
    this._policyZoom = null
    this._zoomBias = 0
    // Undo everything a run put on the camera. `fitBounds` reframes but leaves
    // pitch and bearing where they were, so without this the Lead-in — the
    // state that is meant to look exactly like a freshly loaded page — would
    // come back still tipped over and facing the last POI's heading.
    this._map?.stop()
    // The light goes back to the trip's own, not to plain day: the Lead-in
    // frames the whole trip and so belongs to no particular moment in it, and
    // the light the trip opens on is the one it opened on at load. Day was a
    // claim about the trip rather than the absence of one, and it made a dusk
    // trip open in flat noon and jump at its first POI.
    //
    // The basemap's own labels deliberately do *not* get restored here. Their
    // visibility belongs to the run as a whole, and the Lead-in is a position
    // inside a run as often as it is the end of one — an autoplayed page
    // passes through it just after starting, and restoring them there put
    // Mapbox's labels back up for the entire playback.
    this.setLight(null)
    this._fitViewport({ pitch: 0, bearing: 0 })
  }

  /**
   * Replace the rendered POIs without re-framing the map.
   *
   * The `pois` attribute is the page's payload, so a client-side edit that
   * moves a POI cannot write it back through the attribute: assigning the
   * property refits the viewport and yanks the camera away from whatever the
   * viewer was looking at. This is the live-edit door — markers follow the new
   * coordinates, the camera stays put.
   */
  setPois(json: SerializedJson<MapPoi[]>): void {
    this._skipFitForPois = true
    this.poisJson = json
  }

  /**
   * Show only the marker the camera is on, or bring the rest back.
   *
   * A halo is a *comparative* signal: it works by being brighter than its
   * neighbours, which is exactly the job it cannot do inside a cluster of
   * near-identical pins. During a run there is only one POI that matters and
   * no reason to draw the others at all — removing them is unambiguous where
   * emphasising one is not.
   *
   * Desired state rather than a command, because markers are rebuilt whenever
   * the POI list changes and a re-render must not quietly un-hide them.
   */
  setSoloMarker(solo: boolean): void {
    if (this._soloMarker === solo) return
    this._soloMarker = solo
    this._applyMarkerTreatment()
  }

  /**
   * Move the halo to `id`'s marker, or clear it.
   *
   * Clearing leaves the marker the camera is still looking at faded rather
   * than plain: a step onto a POI with no coordinates has nowhere to point,
   * and the camera has not gone anywhere.
   */
  private _applyHalo(id: string | null) {
    if (id !== null) this._cameraPoiId = id
    this._haloed = id !== null
    this._applyMarkerTreatment()
  }

  /**
   * Put every marker into the state the camera and the solo flag imply.
   *
   * One pass over all of them, so there is no way for a marker to keep a
   * treatment that belonged to a previous POI. Exactly one marker ever carries
   * a halo or a fade.
   *
   * Solo is ignored while the camera has no POI — the Lead-in frames the whole
   * trip, and a run that passes through it would otherwise blank the map.
   */
  private _applyMarkerTreatment(): void {
    const subject = this._cameraPoiId
    const solo = this._soloMarker && subject !== null
    for (const [id, marker] of this._markersById) {
      const el = marker.getElement()
      if (!el) continue
      el.classList.remove("is-haloed", "is-dimmed", "is-hidden")
      if (id === subject) {
        el.classList.add(this._haloed ? "is-haloed" : "is-dimmed")
      } else if (solo) {
        el.classList.add("is-hidden")
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Viewport fitting
  // ---------------------------------------------------------------------------

  private _computeInitialBounds(): mapboxgl.LngLatBounds | null {
    const route = this._parseRoute()
    if (route && route.coordinates.length > 0) {
      const bounds = new mapboxgl.LngLatBounds()
      for (const segment of route.coordinates) {
        for (const [lng, lat] of segment) bounds.extend([lng, lat])
      }
      if (!bounds.isEmpty()) return bounds
    }

    const all = [...this._parsePois(), ...this._parseBoundsPois()]
    if (all.length === 0) return null

    const bounds = new mapboxgl.LngLatBounds()
    for (const p of all) {
      const at = poiLngLat(p)
      if (at) bounds.extend([at.lng, at.lat])
    }
    return bounds.isEmpty() ? null : bounds
  }

  private _parseBoundsPois(): MapPoi[] {
    return parseMapPois(this.boundsPoisJson)
  }

  /**
   * Frame the whole trip.
   *
   * `camera` is only passed by `resetViewport`, which needs the pitch and
   * bearing levelled as well as the bounds refit. The re-render paths leave it
   * off, so adding a POI does not yank a map the viewer has tilted.
   */
  private _fitViewport(camera?: { pitch?: number; bearing?: number }) {
    if (!this._map) return

    const bounds = this._computeInitialBounds()
    if (!bounds) return

    // Single point: setCenter + setZoom instead of fitBounds-on-zero-area
    const sw = bounds.getSouthWest()
    const ne = bounds.getNorthEast()
    if (sw.lng === ne.lng && sw.lat === ne.lat) {
      this._map.setCenter([sw.lng, sw.lat])
      this._map.setZoom(13)
      if (camera?.pitch !== undefined) this._map.setPitch(camera.pitch)
      if (camera?.bearing !== undefined) this._map.setBearing(camera.bearing)
      return
    }

    this._map.fitBounds(bounds, { padding: 60, maxZoom: 14, ...camera })
  }

  override render() {
    return html`
      <div
        class="trip-map-container"
        style="width: 100%; height: ${this
          .height}; border-radius: 8px; overflow: hidden;"
      ></div>
    `
  }
}

function esc(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
}

declare global {
  interface HTMLElementTagNameMap {
    "trip-map": TripMap
  }
}
